import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import EnablementCard from './EnablementCard';
import { CardData, EditType } from '../types';

interface EnablementSectionProps {
  sectionKey: 'embedded' | 'extended' | 'custom';
  label: string;
  iconSrc: string;
  agents: CardData[];
  bgcolor: string;
  skeletonBgcolor: string;
  skeletonCount: number;
  isLoading: boolean;
  isExpanded: boolean;
  isEditing: boolean;
  onToggle: () => void;
  onEdit: (type: EditType, item?: any) => void;
}

const EnablementSection: React.FC<EnablementSectionProps> = ({
  sectionKey,
  label,
  iconSrc,
  agents,
  bgcolor,
  skeletonBgcolor,
  skeletonCount,
  isLoading,
  isExpanded,
  isEditing,
  onToggle,
  onEdit,
}) => {
  // Hide the section entirely when not loading and there's no data
  if (agents.length === 0 && !isLoading) return null;

  return (
    <Box sx={{ borderBottom: sectionKey !== 'custom' ? '1px solid #DDE1E6' : undefined }}>
      {/* Section header / toggle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          cursor: 'pointer',
        }}
        onClick={onToggle}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="img" src={iconSrc} sx={{ width: 16, height: 16 }} />
          <Typography sx={{ fontWeight: 600, fontSize: '15px' }}>{label}</Typography>
        </Box>
        <Box
          sx={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s',
          }}
        >
          <KeyboardArrowDownIcon />
        </Box>
      </Box>

      {/* Section body */}
      {isExpanded && (
        <Box sx={{ px: 2, pb: 2 }}>
          {isLoading ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                gap: 2,
              }}
            >
              {Array.from({ length: skeletonCount }).map((_, i) => (
                <Box key={i} sx={{ p: 2, borderRadius: '8px', bgcolor: skeletonBgcolor }}>
                  <Skeleton variant="text" width="60%" height={20} />
                  <Skeleton variant="text" width="100%" height={16} sx={{ mt: 1 }} />
                  <Skeleton variant="text" width="100%" height={16} />
                  <Skeleton variant="text" width="80%" height={16} />
                </Box>
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                gap: 2,
              }}
            >
              {agents.map((agent, index) => (
                <Box key={index} sx={{ position: 'relative', borderRadius: '8px' }}>
                  {isEditing && (
                    <DriveFileRenameOutlineIcon
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        cursor: 'pointer',
                        height: 16,
                        width: 16,
                        color: 'grey.500',
                        zIndex: 1,
                      }}
                      onClick={() => onEdit(sectionKey as EditType, { index })}
                    />
                  )}
                  <EnablementCard
                    title={agent.capability_name}
                    content={agent.description}
                    bgcolor={bgcolor}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default EnablementSection;
